import {
  Case,
  CaseUploadRequest,
  CaseUploadResponse,
  FilterParams,
  PaginatedResponse,
  PaginationParams,
} from '@/types';
import api, { handleApiError, uploadFile } from './api';

// Case Management API
export const casesApi = {
  // Get all cases with filtering and pagination
  getCases: async (
    filters?: FilterParams,
    pagination?: PaginationParams
  ): Promise<PaginatedResponse<Case>> => {
    try {
      const params = new URLSearchParams();
      
      if (filters?.search) params.append('search', filters.search);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.date_from) params.append('date_from', filters.date_from);
      if (filters?.date_to) params.append('date_to', filters.date_to);
      if (filters?.sort_by) params.append('sort_by', filters.sort_by);
      if (filters?.sort_order) params.append('sort_order', filters.sort_order);
      
      if (pagination?.page) params.append('page', pagination.page.toString());
      if (pagination?.limit) params.append('limit', pagination.limit.toString());

      const response = await api.get(`/api/v1/cases?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Get a specific case by ID
  getCase: async (caseId: string): Promise<Case> => {
    try {
      const response = await api.get(`/api/v1/cases/${caseId}`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Create a new case and get upload URL
  createCase: async (caseData: Omit<CaseUploadRequest, 'file'>): Promise<CaseUploadResponse> => {
    try {
      const response = await api.post('/api/v1/cases', caseData);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Upload file to the presigned URL
  uploadCaseFile: async (
    uploadUrl: string,
    fields: Record<string, string>,
    file: File,
    onProgress?: (percentage: number) => void
  ): Promise<void> => {
    try {
      await uploadFile(uploadUrl, file, fields, onProgress);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Simple upload case method for direct file upload
  uploadCase: async (formData: FormData): Promise<any> => {
    try {
      const response = await api.post('/api/v1/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Verify file upload completion
  verifyCaseUpload: async (caseId: string): Promise<Case> => {
    try {
      const response = await api.post(`/api/v1/cases/${caseId}/verify-upload`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Update case metadata
  updateCase: async (
    caseId: string,
    updates: Partial<Pick<Case, 'title' | 'description'>>
  ): Promise<Case> => {
    try {
      const response = await api.put(`/api/v1/cases/${caseId}`, updates);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Delete a case
  deleteCase: async (caseId: string): Promise<void> => {
    try {
      await api.delete(`/api/v1/cases/${caseId}`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Get case processing status
  getCaseStatus: async (caseId: string): Promise<{
    case_id: string;
    status: string;
    processing_stage: string;
    completion_percentage: number;
    estimated_completion_time?: string;
    error_details?: string;
  }> => {
    try {
      const response = await api.get(`/api/v1/cases/${caseId}/status`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Get case statistics
  getCaseStatistics: async (): Promise<{
    total_cases: number;
    cases_by_status: Record<string, number>;
    recent_uploads: number;
    total_file_size: number;
    average_processing_time: number;
  }> => {
    try {
      const response = await api.get('/api/v1/cases/statistics');
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Get download URL for case file
  getCaseDownloadUrl: async (caseId: string, expiryHours: number = 24): Promise<{
    case_id: string;
    download_url: string;
    file_name: string;
    file_size: number;
    expires_hours: number;
  }> => {
    try {
      const response = await api.get(`/api/v1/cases/${caseId}/download-url?expiry_hours=${expiryHours}`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Download case file directly
  downloadCaseFile: async (caseId: string, fileName?: string): Promise<void> => {
    try {
      // Try to get download URL first
      try {
        const downloadInfo = await casesApi.getCaseDownloadUrl(caseId);
        
        // Create a temporary link and click it to trigger download
        const link = document.createElement('a');
        link.href = downloadInfo.download_url;
        link.download = fileName || downloadInfo.file_name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
      } catch (signedUrlError) {
        // Fallback to direct download if signed URL fails
        console.warn('Signed URL download failed, trying direct download:', signedUrlError);
        
        const directDownloadUrl = `${api.defaults.baseURL}/cases/${caseId}/download-direct`;
        
        // Create a temporary link and click it to trigger download
        const link = document.createElement('a');
        link.href = directDownloadUrl;
        link.download = fileName || 'case.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Get case parsed content
  getCaseParsedContent: async (caseId: string): Promise<{
    case_id: string;
    page_count: number;
    parsed_at: string;
    processing_time_seconds: number;
    raw_text: string;
    job_id: string;
    sections: Array<{
      page_number: number;
      title: string;
      content: string;
    }>;
  }> => {
    try {
      const response = await api.get(`/api/v1/cases/${caseId}/parsed-content`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Jury simulation methods
  startJurySimulation: async (caseId: string, jurorCount: number = 500): Promise<{
    simulation_id: string;
    case_id: string;
    simulation_status: string;
    juror_count: number;
    started_at: string;
    estimated_completion_minutes: number;
    message: string;
  }> => {
    try {
      const response = await api.post(`https://orchestrator-195480669181.us-central1.run.app/${caseId}/simulate`, {
        juror_count: jurorCount
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  getSimulationStatus: async (caseId: string): Promise<{
    case_id: string;
    overall_status: string;
    parsing_status?: string;
    jury_pool_status?: string;
    simulation_status?: string;
    aggregation_status?: string;
    progress_percentage?: number;
    estimated_completion_minutes?: number;
    results_summary?: any;
  }> => {
    try {
      const response = await api.get(`https://orchestrator-195480669181.us-central1.run.app/${caseId}/status`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Get jurors for a specific case
  getCaseJurors: async (caseId: string, limit: number = 500): Promise<{
    case_id: string;
    total_pool_size: number;
    returned_count: number;
    jurors: Array<{
      juror_id: string;
      name: string;
      age: number;
      gender: string;
      race_ethnicity: string;
      education: string;
      political_leaning: string;
    }>;
  }> => {
    try {
      // First check if jury pool exists, if not generate it
      const statusResponse = await api.get(`https://demographics-195480669181.us-central1.run.app/${caseId}/status`);
      const status = statusResponse.data.status;
      
      if (status === 'not_started') {
        // Generate jury pool first
        await api.post(`https://demographics-195480669181.us-central1.run.app/${caseId}`, {
          pool_size: 500,
          force_regenerate: false
        });
      }
      
      // Now get the actual jurors
      const response = await api.get(`https://demographics-195480669181.us-central1.run.app/${caseId}/jurors?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};
